import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { COLLECTIONS, NOOP } from "constants/action-types";
import { CollectionKeys, Item, ITEMS_ID } from "constants/product-collection";
import { addHashtags, IMutateCollectionItemPayload, mutateCollectionItem } from "actions/collections";
import { listSetOperation } from "actions/mutate";
import regexes from "components/draft-js-mention/utils/hashtagRegex";
// error tacking
import { fromJS, List, Map } from "immutable";
import diff from "immutablediff";
import { internalGetter, ISchemaKey } from "helpers/product-collection";
import { ActionsObservable } from "redux-observable";
import { IStore } from "reducers";
import { GridApi } from "ag-grid-community";
import { makeAutoSelectSingleResultEpicActions } from "./helpers/collection";

type Payload = {
    rowId: string;
    rowIndex: number;
    dependentColumnId: string;
};

const makeMutateCollectionItemToEmpty = (ITEMS_ID: string, rowIndex: number, data: Map<any, any>, fieldId: string) => {
    return [
        mutateCollectionItem({
            itemsId: ITEMS_ID,
            index: rowIndex,
            item: data.deleteIn(["fields", fieldId]),
        }),
    ];
};

//TODO: NU-33538 UNIT TESTS
export const autoSelectSingleResultEpic = (
    action$: ActionsObservable<{
        type: string;
        payload: Payload;
    }>,
    store$: { getState: () => IStore },
) =>
    action$.ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.SYNC_AUTO_SELECT_SINGLE_RESULT).flatMap(({ payload }) => {
        const state = store$.getState();
        const {
            mutate,
            collection: { collectionId, merchantId, gridApi },
        } = state;
        const api = gridApi as GridApi;
        const rowNode = api.getRowNode(payload.rowId);

        const collection = mutate.snapshots.get(collectionId);
        const itemDisplayProperty: List<Map<keyof ISchemaKey, any>> = collection.getIn(
            CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS,
        );
        const column = itemDisplayProperty.find(
            (schemaKey) => schemaKey.get("field_name") === payload.dependentColumnId,
        );
        const { interalLookup, dependency } = internalGetter(
            column.toJS(),
            merchantId,
            itemDisplayProperty.toJS(),
        )(rowNode.data.fields);
        // dependency here is a object, if any value is null we can bailout and not make the call
        const dependenciesValues = Object.values(dependency);

        if (dependenciesValues.some((value) => !value)) {
            // TODO: NU-33538 do not mutate if the values are the same
            return makeMutateCollectionItemToEmpty(
                ITEMS_ID,
                payload.rowIndex,
                fromJS(rowNode.data),
                payload.dependentColumnId,
            );
        }
        return Observable.fromPromise(
            interalLookup({ data: dependency, fieldName: payload.dependentColumnId }),
        ).flatMap((result: Record<string, any>) => {
            // we need to get store again
            // because the instance in the previous observable is old
            const state = store$.getState();
            const {
                collection: { gridApi },
            } = state;
            const api = gridApi as GridApi;
            const rowNode = api.getRowNode(payload.rowId);
            const immutableData = fromJS(rowNode.data) as Map<any, any>;

            if (result.length === 1) {
                return [
                    mutateCollectionItem({
                        itemsId: ITEMS_ID,
                        index: payload.rowIndex,
                        item: immutableData.setIn(["fields", payload.dependentColumnId, "string_value"], result[0].key),
                    }),
                ];
            }
            if (result.length === 0) {
                return makeMutateCollectionItemToEmpty(
                    ITEMS_ID,
                    payload.rowIndex,
                    immutableData,
                    payload.dependentColumnId,
                );
            }
            if (result.length > 1) {
                const currentValue = immutableData.getIn(["fields", payload.dependentColumnId]);
                const possibleResults = result.map(({ key }) => key);
                if (currentValue && !possibleResults.includes(currentValue.get("string_value"))) {
                    return makeMutateCollectionItemToEmpty(
                        ITEMS_ID,
                        payload.rowIndex,
                        immutableData,
                        payload.dependentColumnId,
                    );
                }
            }

            // we do nothing when there is more than one possible option and the value
            return [{ type: NOOP }];
        });
    });

export const mutateItemInCollectionEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEM.MUTATE)
        .map(({ payload }) => payload)
        .flatMap(({ itemsId, index, item, value, meta, fieldName }: IMutateCollectionItemPayload) => {
            const state = store.getState();
            const {
                mutate,
                collection: { dependentFieldsLookupMap },
            } = state;

            let mutatedItem;
            if (mutate && mutate.snapshots) {
                // This is in case a set op occurs before receiving the remove op from server
                const currentItems = mutate.snapshots.get(itemsId);
                // This is a source of slowdowns but nessesary evil for now
                mutatedItem = currentItems.find(
                    (currentItem) =>
                        currentItem.getIn(["fields", "id", "string_value"]) ===
                        item.getIn(["fields", "id", "string_value"]),
                );

                if (!mutatedItem) {
                    return [];
                }
            }
            const hashtags = [];
            if (value) {
                let match;

                while ((match = regexes.validHashtag.exec(value))) {
                    const hashtag = match[3] as string;
                    hashtags.push(hashtag);
                }
            } else {
                item.get("fields").forEach((v) => {
                    v.forEach((subvalue) => {
                        let match;

                        while ((match = regexes.validHashtag.exec(subvalue))) {
                            const hashtag = match[3] as string;
                            hashtags.push(hashtag);
                        }
                    });
                });
            }

            let deltas;
            if (mutatedItem) {
                deltas = diff(mutatedItem, item);
            }
            const actions = [];

            if (hashtags.length) {
                actions.push(addHashtags(hashtags));
            }

            actions.push(listSetOperation(itemsId, index, item, deltas, meta));

            const singleSelectFieldsActions = makeAutoSelectSingleResultEpicActions(
                fieldName,
                dependentFieldsLookupMap,
                {
                    rowIndex: index,
                    rowId: item.getIn(Item.FIELDS.ID),
                },
            );
            return actions.concat(singleSelectFieldsActions);
        });
